import React, { useEffect } from "react";
import {
    Box,
    Header,
    Table,
    Button,
    SpaceBetween,
    Spinner
} from "@amzn/awsui-components-react/polaris";
import { downloadTaskFile as downloadTaskFileByTaskId } from "../../excalibur/components/excaliburAPIs";
import { formatDateWithTime } from "../../../common/utils/apiUtils";
import { statusColorCodes } from '../../../common/utils/commonUtils';
import FileDownloadButton from "../../../common/ellipsisText/FileDownloadButton";
import WindowPagination from "../../../common/pagination/WindowPagination";
import { getPageItems } from "../../../common/pagination/paging";
import {downloadFolder, uploadFiles} from "../../../common/utils/s3Utils";
import {
    ARTIFACTS_FORMAT,
    COLOR_RED,
    TRANSACTION_DETAIL_INFO
} from "../../../common/constants/constants";
import AppConfig from "../../../AppConfig";
import {getFilingReceipt} from "../utils/filingAPIs";
import {generateErrorDetailsMessage, getFileNameFromS3Key, hasSubmissionReceipt} from "../utils/Utils";
import Link from "@amzn/awsui-components-react/polaris/link";
import ColumnHeader from "../../../common/table/ColumnHeader";
import StatusInfo from "../../../common/table/StatusInfo";
import { FILING_COLUMN_INFO, FILING_STATUS_INFO } from "../../../common/constants/columnInfo";
import { ROUTE_PATH_TRANSACTION_DETAILS_PAGE } from "../../../common/constants/route_paths";
import { useNavigate, useLocation } from "react-router-dom";
import { getTransactionDetailColumnDefinitions, transactionDetailVisibleColumns, isTransformationRoute, taskDownloadKey } from "./filingTableConfig";

const Actiontable = (params) => {

    const { table, tableDispatcher, tableHeader, visibleColumns, transactionId, clientId, tableContext, parentAuditRow } = params;
    const [loading, setLoading] = React.useState(false);
    // Names the single download cell whose request is in flight, so only that cell is disabled
    // rather than every download button on the page.
    const [downloadingTaskKey, setDownloadingTaskKey] = React.useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    const downloadArtifacts = async (inputFileKey, s3Bucket) => {
        setLoading(true);
        try {
            await downloadFolder(inputFileKey, s3Bucket, params.domain)
        } catch (error) {
            tableDispatcher({
                type: "SET_FLASH_BAR", payload: {
                    msg: 'Error occured while downloading the file',
                    type: 'error',
                    dispatcher: tableDispatcher
                }
            })
        }
        setLoading(false);
    }

    // Per-task download for the Transaction Details page. Unlike downloadArtifacts (which reads
    // S3 directly with the browser's credentials), this asks TwinService for an Excalibur-signed
    // URL scoped to one taskId, so the website needs no S3 permission of its own.
    const downloadTaskFile = async (taskId, fileType) => {
        setLoading(true);
        setDownloadingTaskKey(taskDownloadKey(taskId, fileType));
        try {
            await downloadTaskFileByTaskId({
                transactionId,
                taskId,
                fileType,
                clientId
            });
        } catch (error) {
            tableDispatcher({
                type: "SET_FLASH_BAR", payload: {
                    msg: 'Error occured while downloading the file',
                    type: 'error',
                    dispatcher: tableDispatcher
                }
            })
        } finally {
            // Reset in finally so a throw anywhere above -- including from the error dispatch
            // itself -- cannot leave the spinner up and the cell permanently unclickable.
            setLoading(false);
            setDownloadingTaskKey(null);
        }
    }

    const downloadSubmissionReceipt = (audit) => {
        if(audit.generatedFileKey){
            downloadArtifacts(audit.generatedFileKey, AppConfig[params.domain].FILING_RECEIPT_BUCKET);
        } else {
            getFilingReceipt(tableDispatcher, audit.useCase, audit.subUseCase, audit.transactionId, params.domain, setLoading);
        }
    }

    const uploadArtifacts = async (audit, files) => {
        setLoading(true);
        try {
            await uploadFiles(files, AppConfig[params.domain].ARTIFACTS_BUCKET, audit.inputFileKey, params.domain)
        } catch (error) {
            tableDispatcher({
                type: "SET_FLASH_BAR", payload: {
                    msg: 'Error occured while uploading the file',
                    type: 'error',
                    dispatcher: tableDispatcher
                }
            })
        }
        setLoading(false);
    }

    const navigateToTransactionDetailsPage = (transaction) => {
        navigate(ROUTE_PATH_TRANSACTION_DETAILS_PAGE, {
            state: {
                data: transaction
            }
        });
    }

    const renderErrorDetails = (record) => {
        if (record.status === 'FAILURE') {
            return (
                <SpaceBetween direction="horizontal" size="xs">
                    <div style={{ color: COLOR_RED }}>{generateErrorDetailsMessage(record.additionalDataMap)}</div>
                    <Link onClick={() => navigateToTransactionDetailsPage(record)}>More Info</Link>
                </SpaceBetween>
            );
        }
        return null;
    };

    // Both lists page through the shared reducer; the context picks the backend call.
    useEffect(() => {
        if (table.initialize) {
            tableDispatcher({ type: "INITIALIZE_AUDIT", payload: { dispatcher: tableDispatcher, tableContext, transactionId } });
        }
    }, [table.initialize, tableContext, transactionId, tableDispatcher]);

    // Dedicated per-task column set for the Transaction Details page. The route decides what the
    // second download column means -- see isTransformationRoute; the parent record decides the first.
    const isTransformation = isTransformationRoute(location?.pathname);
    const transactionDetailColumnDefinitions = getTransactionDetailColumnDefinitions({
        downloadTaskFile,
        isTransformation,
        downloadingTaskKey,
        parentAuditRow
    });

    const isTransactionDetail = tableContext === TRANSACTION_DETAIL_INFO;
    const currentPageItems = getPageItems(table.auditRows, table.currentPageIndex);
    // The parent audit record has a different schema than a task row (inputFileKey
    // instead of inputFilePath, and no taskId), so normalize it onto the task-row
    // shape before using it as a fallback so the task columns resolve.
    const normalizedParentRow = parentAuditRow
        ? {
            ...parentAuditRow,
            taskId: parentAuditRow.taskId || parentAuditRow.transactionId,
            inputFilePath: parentAuditRow.inputFilePath || parentAuditRow.inputFileKey,
            inputFileBucket: parentAuditRow.inputFileBucket
        }
        : null;
    // When the fetched task list is genuinely empty (first page only), fall back to a
    // single row built from the parent audit record so its status / errorDetails /
    // input file are still shown.
    const tableItems = (isTransactionDetail
        && !table.loading
        && table.currentPageIndex === 1
        && table.auditRows.length === 0
        && normalizedParentRow)
        ? [normalizedParentRow]
        : currentPageItems;

    const pageChangePayload = pageIndex => ({
        pageIndex, dispatcher: tableDispatcher, tableContext, transactionId
    });

    return (
        <>
            {loading ? <Spinner className={"appLoader"} size="large" variant={"disabled"}>Loading</Spinner> : null}
        <Table
            loading={table.loading}
            loadingText={table.loadingText}
            onSortingChange={((event)=>{
                tableDispatcher({type:"AUDIT_SORTING_CHANGED",
                    payload:{isDescending:event.detail.isDescending,
                        sortingField:event.detail.sortingColumn.sortingField}});
            })}
            sortingColumn={table.sortingColumn}
            sortingDescending={table.sortingDescending}
            resizableColumns
            columnDefinitions={isTransactionDetail ? transactionDetailColumnDefinitions : [

                {
                    id: "useCase",
                    header: <ColumnHeader title="Country" info={FILING_COLUMN_INFO.useCase} />,
                    cell: record => record.useCase,
                    sortingField: "useCase",
                    width: 150,
                    minWidth: 150
                },
                {
                    id: "subUseCase",
                    header: <ColumnHeader title="Filing Type" info={FILING_COLUMN_INFO.subUseCase} />,
                    cell: record => record.subUseCase,
                    sortingField: "subUseCase",
                    width: 180,
                    minWidth: 180
                },
                {
                    id: "taxIdentifier",
                    header: <ColumnHeader title="Tax Identifier" info={FILING_COLUMN_INFO.taxIdentifier} />,
                    cell: record => record.additionalDataMap?.VRN,
                    width: 150,
                    minWidth: 150
                },
                {
                    id: "status",
                    header: <ColumnHeader title="Status" infoLabel={FILING_COLUMN_INFO.status}
                                          info={<StatusInfo intro={FILING_COLUMN_INFO.status} statuses={FILING_STATUS_INFO} />} />,
                    cell: record => (
                        <SpaceBetween direction="vertical" size="xxs">
                            <div style={statusColorCodes(record.status)}>{record.status}</div>
                            <Link onClick={() => navigateToTransactionDetailsPage(record)}>More info</Link>
                        </SpaceBetween>
                    ),
                    width: 250,
                    minWidth: 250
                },
                {
                    id: "startTimestamp",
                    header: <ColumnHeader title="Request Created on" info={FILING_COLUMN_INFO.startTimestamp} />,
                    cell: record => formatDateWithTime(record.startTimestamp),
                    sortingField: "startTimestamp",
                    width: 230,
                    minWidth: 230
                },
                {
                    id: "createdBy",
                    header: <ColumnHeader title="Submitted By" info={FILING_COLUMN_INFO.createdBy} />,
                    cell: record => record.createdBy,
                    width: 190,
                    minWidth: 190
                },
                {
                    id: "inputFileKey",
                    header: <ColumnHeader title="Input File" info={FILING_COLUMN_INFO.inputFileKey} />,
                    cell: record => (
                        <FileDownloadButton fileName={getFileNameFromS3Key(record.inputFileKey)} fallback="File"
                                            onClick={() => downloadArtifacts(record.inputFileKey, record.inputFileBucket)} />
                    ),
                    width: 260,
                    minWidth: 260
                },
                {
                    id: "submissionDetails",
                    header: "Submission Details",
                    cell: record => JSON.stringify(record.additionalDataMap),
                },
                {
                    id: "getReceipt",
                    header: <ColumnHeader title="Submission Receipt" info={FILING_COLUMN_INFO.getReceipt} />,
                    cell: record => hasSubmissionReceipt(record)
                        ? <FileDownloadButton fileName={getFileNameFromS3Key(record.generatedFileKey)} fallback="Receipt"
                                              onClick={() => downloadSubmissionReceipt(record)} />
                        : null,
                    width: 260,
                    minWidth: 260
                },
                {
                    id: "errorDetails",
                    header: "Error Details",
                    cell: record => record.errorDetails,
                    width: 400,
                    minWidth: 400
                },
                {
                    id: "transactionDetails",
                    header: <ColumnHeader title="Error Details" info={FILING_COLUMN_INFO.transactionDetails} />,
                    cell: record => renderErrorDetails(record),
                    width: 400,
                    minWidth: 400
                },
                {
                    id: "txnArtifacts",
                    header: <ColumnHeader title="Artifacts" info={FILING_COLUMN_INFO.txnArtifacts} />,
                    cell: record => {
                        return (
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button iconName="download"  onClick={() =>
                                    downloadArtifacts(record.inputFileKey, AppConfig[params.domain].ARTIFACTS_BUCKET)}>View</Button>
                                <Button iconName="upload" variant="primary">
                                    <input className="inputButton hidden" type={"file"} accept={ARTIFACTS_FORMAT} name="file" onChange={(event) => uploadArtifacts(record, event.target.files)} multiple/>
                                    Upload
                                </Button>
                            </SpaceBetween>
                        )
                    },
                    minWidth: 300
                }
            ]}
            visibleColumns={isTransactionDetail ? transactionDetailVisibleColumns : visibleColumns}
            empty={
                <Box textAlign="center" color="inherit">
                    <b>No files</b>
                    <Box
                        padding={{ bottom: "s" }}
                        variant="p"
                        color="inherit">
                        No files to display.
                    </Box>

                </Box>
            }
            header={
                <Header
                    variant="h2"
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <WindowPagination
                                currentPageIndex={table.currentPageIndex}
                                pagesCount={table.pageCount}
                                // Dots after the last known page while the backend still has rows to give.
                                openEnd={Boolean(table.lastEvaluatedKey)}
                                onChange={event => {
                                    tableDispatcher({type:"PAGE_INDEX_CHANGED",
                                        payload: pageChangePayload(event.detail.currentPageIndex)})
                                }
                                }
                                ariaLabels={{
                                    nextPageLabel: "Next page",
                                    previousPageLabel: "Previous page",
                                    pageLabel: pageNumber =>
                                        `Page ${pageNumber} of all pages`
                                }}
                            />
                            <Button iconName="refresh" variant="icon"
                                    onClick={()=>{
                                        tableDispatcher({type:"REFRESH_AUDIT",payload:{dispatcher:tableDispatcher,
                                            tableContext:tableContext, transactionId:transactionId}})
                                    }}
                            />
                        </SpaceBetween>
                    }>
                    {tableHeader}
                </Header>
            }
            items={tableItems}
        />
        </>
    );
}

export default Actiontable;